import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../firebase";
import {
  BATCH_WRITE_SIZE,
  COL_SALES_IMPORT_MONTHS,
  LINE_CHUNK_MAX_ROWS,
  LINE_CHUNK_TARGET_BYTES,
  SALES_IMPORT_LINE_CHUNKS_SUB,
} from "../constants";
import { formatFirebaseError } from "../utils/firebaseError";
import { salesImportMonthDocId } from "../utils/weekUtils";
import { rowMatchesDateRange } from "../utils/dateParse";
import { clearImportMonthlyReportCache } from "../../../services/monthlyAutosReportService";
import { rowToFirestorePayload } from "./validationService";

function chunkRowsByApproxBytes(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const chunks = [];
  let current = [];
  let currentBytes = 0;
  for (const row of rows) {
    const rowBytes = JSON.stringify(row).length;
    const wouldExceedBytes = current.length > 0 && currentBytes + rowBytes > LINE_CHUNK_TARGET_BYTES;
    const wouldExceedRows = current.length >= LINE_CHUNK_MAX_ROWS;
    if (wouldExceedBytes || wouldExceedRows) {
      chunks.push(current);
      current = [row];
      currentBytes = rowBytes;
    } else {
      current.push(row);
      currentBytes += rowBytes;
    }
  }
  if (current.length) chunks.push(current);
  return chunks;
}

function chunkDocId(category, index) {
  return `${category}_${index}`;
}

export function salesTransactionsWeekCategory(weekOfMonth) {
  return `transactions_w${weekOfMonth}`;
}

async function listChunkRefsForCategory(monthId, category) {
  const colRef = collection(db, COL_SALES_IMPORT_MONTHS, monthId, SALES_IMPORT_LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  return snap.docs.filter((d) => (d.data()?.category || "") === category).map((d) => d.ref);
}

async function deleteChunkRefs(refs) {
  for (let i = 0; i < refs.length; i += BATCH_WRITE_SIZE) {
    const batch = writeBatch(db);
    for (const ref of refs.slice(i, i + BATCH_WRITE_SIZE)) {
      batch.delete(ref);
    }
    await batch.commit();
  }
}

/**
 * Replace one week slice in lineChunks with validated transaction rows.
 * Writes new chunks first, then removes old ones (safer if upload fails mid-way).
 */
export async function saveSalesImportLineChunks({
  year,
  monthIndex,
  weekOfMonth,
  rows,
  uploadedBy,
  audit = {},
  onProgress,
}) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const category = salesTransactionsWeekCategory(weekOfMonth);
  const uploadedAt = new Date().toISOString();

  const oldRefs = await listChunkRefsForCategory(monthId, category);

  const weekFrom = audit.fromDate || "";
  const weekTo = audit.toDate || "";
  const payloads = (rows || [])
    .filter((r) => r.valid !== false)
    .filter((r) => rowMatchesDateRange(r, weekFrom, weekTo))
    .map((r) =>
      rowToFirestorePayload(r, uploadedBy, uploadedAt, {
        ...audit,
        year,
        monthIndex,
        weekOfMonth,
        periodKey: audit.periodKey,
      })
    );

  if (!payloads.length) {
    const rangeHint =
      weekFrom && weekTo ? ` TranDate must be ${weekFrom} → ${weekTo}.` : "";
    throw new Error(
      `No rows to save for week ${weekOfMonth}.${rangeHint} Check Excel dates or selected month/week.`
    );
  }

  const rowChunks = chunkRowsByApproxBytes(payloads);
  const chunkWrites = rowChunks.map((chunkRows, index) => ({
    id: chunkDocId(category, index),
    data: {
      category,
      index,
      rows: chunkRows,
      periodKey: audit.periodKey || "",
      fromDate: audit.fromDate || "",
      toDate: audit.toDate || "",
      weekOfMonth,
      year,
      month: monthIndex + 1,
    },
  }));

  if (!chunkWrites.length) {
    throw new Error(
      `Refusing to save week with no chunks (transactions_w${weekOfMonth}). Data was not modified.`
    );
  }

  const newChunkIds = new Set(chunkWrites.map((w) => w.id));

  for (let i = 0; i < chunkWrites.length; i += 1) {
    const w = chunkWrites[i];
    try {
      await setDoc(doc(db, COL_SALES_IMPORT_MONTHS, monthId, SALES_IMPORT_LINE_CHUNKS_SUB, w.id), w.data);
    } catch (e) {
      throw new Error(
        `Chunk ${i + 1}/${chunkWrites.length} failed (${w.data.rows?.length || 0} rows): ${formatFirebaseError(e)}`
      );
    }
    if (onProgress) onProgress(i + 1, chunkWrites.length);
    if (i % 5 === 4) await new Promise((r) => setTimeout(r, 0));
  }

  // Only remove chunk docs that are no longer used (same ids were just overwritten via setDoc).
  const refsToDelete = oldRefs.filter((ref) => !newChunkIds.has(ref.id));
  if (refsToDelete.length) {
    await deleteChunkRefs(refsToDelete);
  }

  const rootRef = doc(db, COL_SALES_IMPORT_MONTHS, monthId);
  const logKey = `weeklyLog_${weekOfMonth}`;
  await setDoc(
    rootRef,
    {
      year,
      month: monthIndex + 1,
      updatedAt: serverTimestamp(),
      [logKey]: {
        uploadedAt,
        uploadedBy: uploadedBy || "",
        fileName: audit.fileName || "",
        rowCount: payloads.length,
        periodKey: audit.periodKey || "",
        fromDate: audit.fromDate || "",
        toDate: audit.toDate || "",
        importBatchId: audit.importBatchId || "",
      },
    },
    { merge: true }
  );

  clearImportMonthlyReportCache();

  return { chunkCount: chunkWrites.length, rowCount: payloads.length };
}

export async function deleteSalesImportLineChunksForWeek(year, monthIndex, weekOfMonth) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const category = salesTransactionsWeekCategory(weekOfMonth);
  const refs = await listChunkRefsForCategory(monthId, category);
  if (refs.length) await deleteChunkRefs(refs);
}

/**
 * Fast path: patch one voucher's lines inside existing week chunks (no full-week rewrite).
 * @param {(row: object) => boolean} matchesRow — rows to remove before appending new payloads
 */
export async function patchVoucherLinesInWeekLineChunks({
  year,
  monthIndex,
  weekOfMonth,
  voucherNo,
  newRowPayloads = [],
  matchesRow,
  audit = {},
}) {
  const monthId = salesImportMonthDocId(year, monthIndex);
  const category = salesTransactionsWeekCategory(weekOfMonth);
  const colRef = collection(db, COL_SALES_IMPORT_MONTHS, monthId, SALES_IMPORT_LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  const chunkDocs = snap.docs
    .filter((d) => (d.data()?.category || "") === category)
    .sort((a, b) => Number(a.data()?.index ?? 0) - Number(b.data()?.index ?? 0));

  const payloads = Array.isArray(newRowPayloads) ? newRowPayloads : [];
  if (!chunkDocs.length) {
    if (!payloads.length) {
      return { chunksUpdated: 0, removed: 0, added: 0 };
    }
    const chunkId = chunkDocId(category, 0);
    const chunkData = {
      category,
      index: 0,
      rows: payloads,
      periodKey: audit.periodKey || "",
      fromDate: audit.fromDate || "",
      toDate: audit.toDate || "",
      weekOfMonth,
      year,
      month: monthIndex + 1,
    };
    await setDoc(doc(db, COL_SALES_IMPORT_MONTHS, monthId, SALES_IMPORT_LINE_CHUNKS_SUB, chunkId), chunkData);
    const logKey = `weeklyLog_${weekOfMonth}`;
    await setDoc(
      doc(db, COL_SALES_IMPORT_MONTHS, monthId),
      {
        year,
        month: monthIndex + 1,
        updatedAt: serverTimestamp(),
        [logKey]: {
          uploadedAt: audit.uploadedAt || new Date().toISOString(),
          uploadedBy: audit.uploadedBy || "",
          fileName: audit.fileName || "billing_save",
          rowCount: payloads.length,
          periodKey: audit.periodKey || "",
          fromDate: audit.fromDate || "",
          toDate: audit.toDate || "",
          importBatchId: audit.importBatchId || "",
        },
      },
      { merge: true }
    );
    return { chunksUpdated: 1, removed: 0, added: payloads.length, createdWeek: true };
  }

  const vn = String(voucherNo || "").trim();
  const matchFn =
    typeof matchesRow === "function"
      ? matchesRow
      : (r) => String(r?.voucherNo || "").trim() === vn;

  let removed = 0;
  const updates = [];

  for (const d of chunkDocs) {
    const data = d.data() || {};
    const rows = Array.isArray(data.rows) ? data.rows : [];
    const filtered = rows.filter((r) => {
      if (matchFn(r)) {
        removed += 1;
        return false;
      }
      return true;
    });
    if (filtered.length !== rows.length) {
      updates.push({ ref: d.ref, data: { ...data, rows: filtered } });
    }
  }

  if (payloads.length) {
    if (updates.length) {
      updates[0].data.rows = [...(updates[0].data.rows || []), ...payloads];
    } else {
      const last = chunkDocs[chunkDocs.length - 1];
      const data = last.data() || {};
      updates.push({
        ref: last.ref,
        data: { ...data, rows: [...(data.rows || []), ...payloads] },
      });
    }
  }

  if (!updates.length) {
    return { chunksUpdated: 0, removed, added: 0 };
  }

  for (let i = 0; i < updates.length; i += BATCH_WRITE_SIZE) {
    const batch = writeBatch(db);
    for (const u of updates.slice(i, i + BATCH_WRITE_SIZE)) {
      batch.set(u.ref, u.data);
    }
    await batch.commit();
  }

  return { chunksUpdated: updates.length, removed, added: payloads.length };
}
